import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  selectIsConnectedToRoom,
  selectLocalPeerRole,
  selectPeers,
  selectPermissions,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";
import { ExitIcon, HangUpIcon } from "@100mslive/react-icons";
import {
  Box,
  Button,
  Dialog,
  Flex,
  IconButton,
  styled,
  Text,
  Tooltip,
} from "@100mslive/react-ui";
import { ToastManager } from "./Toast/ToastManager";
import { DialogContent, DialogRow } from "../primitives/DialogContent";
import { setSession } from "../storage/session-storage";
import { HANDOFF_VARIANTS, HandoffAlertModal } from "./HandoffAlertModal";
import { useNavigation } from "./hooks/useNavigation";
import { getCookie } from "../common/helper";
import { isStreamingKit } from "../common/utils";
import {
  APPOINTMENT_STATUS_100MS_COOKIE,
  AppointmentStatus,
  appointmentTypes,
  FIRST_ONLINE_FU_100MS_COOKIE,
  HANDOFF_WAIT_SECONDS,
  roleTypes,
  RX_PAYABLE_100MS_COOKIE,
} from "../common/constants";

export const LeaveRoom = () => {
  const navigate = useNavigation();
  const [searchParams] = useSearchParams();
  const [showEndRoomModal, setShowEndRoomModal] = useState(false);
  const [lockRoom, setLockRoom] = useState(false);
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const permissions = useHMSStore(selectPermissions);
  const peers = useHMSStore(selectPeers);
  const hmsActions = useHMSActions();
  const role = useHMSStore(selectLocalPeerRole);
  const appointmentStatus = getCookie(APPOINTMENT_STATUS_100MS_COOKIE);
  const appointmentType = searchParams.get("appointment_type");

  // Rx handoff alert: the doctor introduces the care team agent to the patient,
  // which lifts purchase conversion. It fires on every screening call, and on a
  // patient's first online follow-up after offline onboarding. SC is readable
  // straight off the URL; the FU flag is not, so web-provider passes it through a
  // cookie (the iframe is a different origin with no appointment API).
  // The status cookie is only set once an encounter exists, so it doubles as
  // "the Rx was submitted" and picks which of the two nudges to show.
  const [handoffStage, setHandoffStage] = useState(null);
  const [countdownStartedAt, setCountdownStartedAt] = useState(null);
  const [secondsRemaining, setSecondsRemaining] =
    useState(HANDOFF_WAIT_SECONDS);

  const isAgentInCall = peers?.some(peer => peer.roleName === roleTypes.AGENT);
  const isProvider = role?.name === roleTypes.PROVIDER;

  useEffect(() => {
    if (!handoffStage || !countdownStartedAt) {
      return;
    }
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - countdownStartedAt) / 1000);
      const remaining = Math.max(0, HANDOFF_WAIT_SECONDS - elapsed);
      setSecondsRemaining(remaining);
      // The provider was asked to hold for the care team; once the window is up
      // they have done their part, so complete the leave they originally asked for.
      if (remaining === 0) {
        clearInterval(interval);
        setHandoffStage(null);
        leaveRoom();
      }
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handoffStage, countdownStartedAt]);

  // Once the care team is in the room the nudge is moot — let the provider go.
  useEffect(() => {
    if (isAgentInCall) {
      setHandoffStage(null);
    }
  }, [isAgentInCall]);

  const redirectToLeavePage = () => {
    const token = searchParams.get("token");
    const appointmentId = searchParams.get("appointment_id");
    if (token) {
      navigate(
        `/leave?token=${token}&appointment_id=${appointmentId}&role=${role?.name}&appointment_type=${appointmentType}`
      );
    } else {
      navigate("/leave");
    }
    ToastManager.clearAllToast();
  };

  const leaveRoom = () => {
    if (role?.name === roleTypes.PATIENT) {
      setSession("recording", false);
      hmsActions.stopRTMPAndRecording();
    }
    setTimeout(() => {
      hmsActions.leave();
      redirectToLeavePage();
    }, 500);
  };

  const endRoom = () => {
    hmsActions.endRoom(lockRoom, "End Room");
    redirectToLeavePage();
  };

  /**
   * Opens the handoff alert if this leave qualifies, and reports whether it did.
   * On a qualifying call it replaces the generic quit dialog outright, so the
   * caller falls back to its normal flow only when this returns false.
   *
   * Cookies are read at click time, not render time — the provider submits the Rx
   * in the parent app while this iframe sits idle, so a value captured at the last
   * render can be stale by the time they hit leave.
   */
  const openHandoffAlert = () => {
    const needsHandoffAlert =
      isProvider &&
      !isAgentInCall &&
      (appointmentType === appointmentTypes.SCREENING_CALL ||
        getCookie(FIRST_ONLINE_FU_100MS_COOKIE) === "true");
    if (!needsHandoffAlert) {
      return false;
    }
    const rxSubmitted =
      getCookie(APPOINTMENT_STATUS_100MS_COOKIE) ===
      AppointmentStatus.COMPLETED;
    // Nothing payable in the Rx means nothing for the care team to convert, so there is
    // no handoff worth holding the provider for. Only knowable once the Rx is in — until
    // then the amount is unknown, and the nudge to submit it still applies.
    if (rxSubmitted && getCookie(RX_PAYABLE_100MS_COOKIE) !== "true") {
      return false;
    }
    setShowEndRoomModal(false);
    // Rx is already in, so the only thing left is waiting for the care team.
    if (rxSubmitted) {
      setCountdownStartedAt(Date.now());
      setSecondsRemaining(HANDOFF_WAIT_SECONDS);
    }
    setHandoffStage(rxSubmitted ? "waiting" : "start");
    return true;
  };

  const attemptLeave = () => {
    if (!openHandoffAlert()) {
      leaveRoom();
    }
  };

  const attemptEndRoomFlow = () => {
    if (!openHandoffAlert()) {
      setShowEndRoomModal(true);
    }
  };

  const isStreamKit = isStreamingKit();
  if (!permissions || !isConnected) {
    return null;
  }

  return (
    <Fragment>
      {!permissions?.endRoom ? (
        <LeaveIconButton
          variant="danger"
          key="LeaveRoom"
          data-testid="leave_room_btn"
          css={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
        >
          <Tooltip title="Leave Room">
            {!isStreamKit ? (
              <Box onClick={attemptLeave}>
                <HangUpIcon key="hangUp" />
              </Box>
            ) : (
              <Flex gap={2}>
                <Box css={{ "@md": { transform: "rotate(180deg)" } }}>
                  <ExitIcon key="hangUp" />
                </Box>
                <Text
                  css={{ "@md": { display: "none" }, color: "inherit" }}
                  variant="button"
                >
                  Leave Studio
                </Text>
              </Flex>
            )}
          </Tooltip>
        </LeaveIconButton>
      ) : (
        <>
          <Flex>
            <LeaveIconButton
              variant="danger"
              key="LeaveRoom"
              data-testid="leave_room_btn"
            >
              <Tooltip title="Leave Room">
                {!isStreamKit ? (
                  <Box onClick={attemptEndRoomFlow}>
                    <HangUpIcon key="hangUp" />
                  </Box>
                ) : (
                  <Flex gap={2}>
                    <Box css={{ "@md": { transform: "rotate(180deg)" } }}>
                      <ExitIcon key="hangUp" />
                    </Box>
                    <Text
                      css={{ "@md": { display: "none" }, color: "inherit" }}
                      variant="button"
                    >
                      Leave Studio
                    </Text>
                  </Flex>
                )}
              </Tooltip>
            </LeaveIconButton>
          </Flex>
          <Dialog.Root
            open={showEndRoomModal}
            onOpenChange={value => {
              if (!value) {
                setLockRoom(false);
              }
              setShowEndRoomModal(value);
            }}
            css={{
              width: "min(100px, 40%)",
              backgroundColor: "#222222",
              borderRadius: "$1",
            }}
          >
            <DialogContent title="You want to quit ? " Icon={ExitIcon}>
              <Text variant="md" css={{ pt: "8px" }}>
                When you end the session, it closes for everyone, and you can't
                undo it. But if you leave, others can keep going without you.
                <br />
                You can still rejoin in both cases.
              </Text>
              <DialogRow justify="between" align="center" mx="$4">
                <Button
                  variant="primary"
                  onClick={leaveRoom}
                  data-testid="lock_end_room"
                >
                  Leave Session
                </Button>
                {(appointmentStatus === AppointmentStatus.COMPLETED ||
                  appointmentType === appointmentTypes.THERAPY) && (
                  <Button
                    variant="danger"
                    onClick={endRoom}
                    data-testid="lock_end_room"
                  >
                    End Session for all
                  </Button>
                )}
              </DialogRow>
            </DialogContent>
          </Dialog.Root>
        </>
      )}
      <HandoffAlertModal
        open={!!handoffStage}
        stage={handoffStage}
        variant={
          appointmentType === appointmentTypes.SCREENING_CALL
            ? HANDOFF_VARIANTS.SCREENING_CALL
            : HANDOFF_VARIANTS.FIRST_ONLINE_FU
        }
        secondsRemaining={secondsRemaining}
        // Deliberately no "End Session for all" here: a mis-click would close the
        // room for everyone mid-handoff. The provider can still end it from the
        // normal quit dialog, which this modal only replaces on an SC / first online FU.
        onStayOnCall={() => setHandoffStage(null)}
        onLeaveAnyway={() => {
          setHandoffStage(null);
          leaveRoom();
        }}
        onClose={() => setHandoffStage(null)}
      />
    </Fragment>
  );
};

const LeaveIconButton = styled(IconButton, {
  color: "$white",
  h: "$14",
  px: "$8",
  r: "$1",
  bg: "$error",
  "&:not([disabled]):hover": {
    bg: "$errorTint",
  },
  "&:not([disabled]):active": {
    bg: "$errorTint",
  },
  "@md": {
    px: "$4",
    mx: 0,
  },
});
